/*
 * Boston Breaks collection / search pages (Phase B).
 * - Filter & sort panel: sidebar on desktop (applies on change), slide-in drawer
 *   on phones/tablets with a live "Show N results" count before applying.
 * - "Load more": appends the next page of products without a full reload.
 * Everything degrades to a plain GET form and plain pagination links without JS.
 */
(function () {
  'use strict';

  var desktop = window.matchMedia('(min-width: 990px)');

  // Build the results URL from the form, dropping empty values (e.g. blank price inputs).
  function buildUrl(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (String(value).trim() !== '') params.append(key, value);
    });
    var query = params.toString();
    return form.getAttribute('action').split('?')[0] + (query ? '?' + query : '');
  }

  function withSection(url, sectionId) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
  }

  function fetchSection(url, sectionId) {
    return fetch(withSection(url, sectionId), { credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  function initFacets(form) {
    var root = form.closest('[data-section-id]');
    var sectionId = root ? root.getAttribute('data-section-id') : null;
    var openButton = document.querySelector('[data-bb-facets-open][aria-controls="' + form.id + '"]');
    var overlay = form.nextElementSibling && form.nextElementSibling.hasAttribute('data-bb-facets-overlay') ? form.nextElementSibling : null;
    var applyButton = form.querySelector('[data-bb-facets-apply]');
    var sortOnly = form.classList.contains('bb-facets--sort-only');
    var lastFocus = null;
    var countRequest = 0;

    function isDrawer() {
      return !sortOnly && !desktop.matches;
    }

    // From 750px up the theme's #PageContainer has a transform, which pins position: fixed
    // to that container instead of the screen. While the drawer is open it lives directly
    // under <body>; placeholders mark where it goes back when it closes.
    var formHome = null;
    var overlayHome = null;

    function moveToBody() {
      if (formHome) return;
      formHome = document.createComment('bb-facets');
      form.parentNode.insertBefore(formHome, form);
      document.body.appendChild(form);
      if (overlay) {
        overlayHome = document.createComment('bb-facets-overlay');
        overlay.parentNode.insertBefore(overlayHome, overlay);
        document.body.appendChild(overlay);
      }
    }

    function moveBack() {
      if (!formHome) return;
      formHome.parentNode.replaceChild(form, formHome);
      formHome = null;
      if (overlay && overlayHome) {
        overlayHome.parentNode.replaceChild(overlay, overlayHome);
        overlayHome = null;
      }
    }

    function open() {
      lastFocus = document.activeElement;
      moveToBody();
      form.classList.add('is-open');
      form.setAttribute('role', 'dialog');
      form.setAttribute('aria-modal', 'true');
      form.setAttribute('aria-labelledby', form.id + '-title');
      if (overlay) overlay.hidden = false;
      document.documentElement.classList.add('bb-no-scroll');
      if (openButton) openButton.setAttribute('aria-expanded', 'true');
      var closeButton = form.querySelector('[data-bb-facets-close]');
      if (closeButton) closeButton.focus();
    }

    function close() {
      form.classList.remove('is-open');
      form.removeAttribute('role');
      form.removeAttribute('aria-modal');
      form.removeAttribute('aria-labelledby');
      if (overlay) overlay.hidden = true;
      moveBack();
      document.documentElement.classList.remove('bb-no-scroll');
      if (openButton) openButton.setAttribute('aria-expanded', 'false');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    function setApplyLabel(count) {
      if (!applyButton) return;
      if (count === null || isNaN(count)) {
        applyButton.textContent = 'Show results';
      } else if (count === 0) {
        applyButton.textContent = 'No matching items';
      } else if (count === 1) {
        applyButton.textContent = 'Show 1 result';
      } else {
        applyButton.textContent = 'Show ' + count + ' results';
      }
    }

    // Ask Shopify how many results the pending selection would return (honest count).
    function updateCount() {
      if (!sectionId) return setApplyLabel(null);
      var request = ++countRequest;
      if (applyButton) applyButton.setAttribute('aria-busy', 'true');
      fetchSection(buildUrl(form), sectionId).then(function (doc) {
        if (request !== countRequest) return;
        var counter = doc.querySelector('[data-bb-results-count]');
        setApplyLabel(counter ? parseInt(counter.getAttribute('data-count'), 10) : null);
      }).catch(function () {
        if (request === countRequest) setApplyLabel(null);
      }).then(function () {
        if (applyButton) applyButton.removeAttribute('aria-busy');
      });
    }

    form.addEventListener('change', function (event) {
      if (isDrawer()) {
        updateCount();
        return;
      }
      // Desktop: checkboxes and sort apply immediately. Price boxes wait for
      // Enter or their Apply button, so typing a min doesn't reload before the max.
      if (event.target && event.target.type === 'number') return;
      window.location.href = buildUrl(form);
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      window.location.href = buildUrl(form);
    });

    if (openButton) openButton.addEventListener('click', open);
    Array.prototype.forEach.call(form.querySelectorAll('[data-bb-facets-close]'), function (button) {
      button.addEventListener('click', close);
    });
    if (overlay) overlay.addEventListener('click', close);

    form.addEventListener('keydown', function (event) {
      if (!form.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep keyboard focus inside the open drawer.
      var focusable = form.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, summary');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    var onBreakpoint = function () {
      if (desktop.matches && form.classList.contains('is-open')) close();
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
    else if (desktop.addListener) desktop.addListener(onBreakpoint);
  }

  function initLoadMore(root) {
    root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-bb-load-more]');
      if (!link) return;
      var sectionId = root.getAttribute('data-section-id');
      var grid = root.querySelector('[data-bb-grid]');
      var wrap = link.closest('[data-bb-load-more-wrap]');
      if (!sectionId || !grid || !wrap) return;

      event.preventDefault();
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Loading…';

      fetchSection(link.href, sectionId).then(function (doc) {
        var nextGrid = doc.querySelector('[data-bb-grid]');
        var firstNew = null;
        if (nextGrid) {
          Array.prototype.forEach.call(nextGrid.children, function (item) {
            var node = document.importNode(item, true);
            grid.appendChild(node);
            if (!firstNew) firstNew = node;
          });
        }
        // Search: matching pages/articles are listed under the grid; append those too.
        var extra = root.querySelector('[data-bb-grid-extra]');
        var nextExtra = doc.querySelector('[data-bb-grid-extra]');
        if (extra && nextExtra) {
          Array.prototype.forEach.call(nextExtra.children, function (item) {
            extra.appendChild(document.importNode(item, true));
          });
        }
        var nextWrap = doc.querySelector('[data-bb-load-more-wrap]');
        if (nextWrap) {
          wrap.parentNode.replaceChild(document.importNode(nextWrap, true), wrap);
        } else {
          wrap.parentNode.removeChild(wrap);
        }
        // Move keyboard focus to the first newly loaded product.
        var firstLink = firstNew && firstNew.querySelector('a');
        if (firstLink) firstLink.focus({ preventScroll: true });
      }).catch(function () {
        window.location.href = link.href;
      });
    });
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-bb-facets]'), initFacets);
  Array.prototype.forEach.call(document.querySelectorAll('[data-bb-collection]'), initLoadMore);
})();
